import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { BE_API_AUTH_URL } from '../constants/app.constants';
import {
  AdminDashboardActivityResponse,
  AdminBillingConfig,
  AdminDashboardBillingHistoryItem,
  AdminDashboardCharts,
  AdminDashboardCompanyDetail,
  AdminDashboardCompanyResponse,
  AdminDashboardContractSummary,
  AdminDashboardFilters,
  AdminDashboardOverview,
  BillingActionListResponse,
  BillingActionCountResponse,
  BillingActionItem,
  ContractAlertsResponse,
  ContractTimelineResponse,
} from '../admin-dashboard/admin-dashboard.models';
import {
  AdminBillingDocumentsResponse,
  UpdateBillingDocumentPayload,
} from '../models/billing-document.models';

@Injectable({
  providedIn: 'root',
})
export class AdminDashboardService {
  constructor(private http: HttpClient, private authService: AuthService) {}

  private buildParams(filters: AdminDashboardFilters & Record<string, any> = {}): HttpParams {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value === null || typeof value === 'undefined' || value === '') {
        return;
      }
      params = params.set(key, String(value));
    });
    return params;
  }

  getOverview(filters: AdminDashboardFilters): Observable<AdminDashboardOverview> {
    return this.http
      .get<AdminDashboardOverview>(`${BE_API_AUTH_URL}/admin/dashboard/overview`, {
        headers: this.authService.createAuthorizationHeader(),
        params: this.buildParams(filters),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getCharts(filters: AdminDashboardFilters): Observable<AdminDashboardCharts> {
    return this.http
      .get<AdminDashboardCharts>(`${BE_API_AUTH_URL}/admin/dashboard/charts`, {
        headers: this.authService.createAuthorizationHeader(),
        params: this.buildParams(filters),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getCompanies(filters: AdminDashboardFilters & {
    page: number;
    pageSize: number;
    sortField?: string;
    sortOrder?: string;
    search?: string;
  }): Observable<AdminDashboardCompanyResponse> {
    return this.http
      .get<AdminDashboardCompanyResponse>(`${BE_API_AUTH_URL}/admin/dashboard/companies`, {
        headers: this.authService.createAuthorizationHeader(),
        params: this.buildParams(filters),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getContracts(filters: AdminDashboardFilters & {
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortOrder?: string;
    search?: string;
  }): Observable<AdminDashboardContractSummary> {
    return this.http
      .get<AdminDashboardContractSummary>(`${BE_API_AUTH_URL}/admin/dashboard/contracts`, {
        headers: this.authService.createAuthorizationHeader(),
        params: this.buildParams(filters),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getActivity(filters: AdminDashboardFilters & { page?: number; pageSize?: number; limit?: number }): Observable<AdminDashboardActivityResponse> {
    return this.http
      .get<AdminDashboardActivityResponse>(`${BE_API_AUTH_URL}/admin/dashboard/activity`, {
        headers: this.authService.createAuthorizationHeader(),
        params: this.buildParams(filters),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getCompanyDetail(companyId: string, filters: AdminDashboardFilters): Observable<AdminDashboardCompanyDetail> {
    return this.http
      .get<AdminDashboardCompanyDetail>(`${BE_API_AUTH_URL}/admin/dashboard/companies/${companyId}`, {
        headers: this.authService.createAuthorizationHeader(),
        params: this.buildParams(filters),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getCompanyContractTimeline(companyId: string): Observable<ContractTimelineResponse> {
    return this.http
      .get<ContractTimelineResponse>(`${BE_API_AUTH_URL}/admin/dashboard/companies/${companyId}/contract-timeline`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getContractAlerts(): Observable<ContractAlertsResponse> {
    return this.http
      .get<ContractAlertsResponse>(`${BE_API_AUTH_URL}/admin/dashboard/contract-alerts`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getCompanyBillingDocuments(companyId: string, filters: { year?: number | null; type?: string | null } = {}): Observable<AdminBillingDocumentsResponse> {
    return this.http
      .get<AdminBillingDocumentsResponse>(`${BE_API_AUTH_URL.replace('/auth', '')}/platform/admin/companies/${companyId}/billing-documents`, {
        headers: this.authService.createAuthorizationHeader(),
        params: this.buildParams(filters),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  createCompanyBillingDocument(companyId: string, formData: FormData): Observable<any> {
    return this.http
      .post(`${BE_API_AUTH_URL.replace('/auth', '')}/platform/admin/companies/${companyId}/billing-documents`, formData, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  updateBillingDocument(documentId: string, payload: UpdateBillingDocumentPayload): Observable<any> {
    return this.http
      .patch(`${BE_API_AUTH_URL.replace('/auth', '')}/platform/admin/billing-documents/${documentId}`, payload, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  archiveBillingDocument(documentId: string): Observable<any> {
    return this.http
      .delete(`${BE_API_AUTH_URL.replace('/auth', '')}/platform/admin/billing-documents/${documentId}`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getAdminBillingDocumentDownloadUrl(documentId: string): Observable<{ url: string }> {
    return this.http
      .get<{ url: string }>(`${BE_API_AUTH_URL.replace('/auth', '')}/platform/admin/billing-documents/${documentId}/download`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getCompanyBillingHistory(companyId: string): Observable<AdminDashboardBillingHistoryItem[]> {
    return this.http
      .get<AdminDashboardBillingHistoryItem[]>(`${BE_API_AUTH_URL.replace('/auth', '')}/platform/admin/companies/${companyId}/billing-history`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  updateCompanyPricing(companyId: string, payload: {
    customUnitPrice: number | null;
    customUnitPriceValidityUntil: string | null;
  }): Observable<{
    companyId: string;
    customUnitPrice: number | null;
    customUnitPriceValidityUntil: string | null;
    effectiveUnitPrice: number;
  }> {
    return this.http
      .put<{
        companyId: string;
        customUnitPrice: number | null;
        customUnitPriceValidityUntil: string | null;
        effectiveUnitPrice: number;
      }>(`${BE_API_AUTH_URL}/admin/companies/${companyId}/pricing`, payload, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getBillingConfig(): Observable<AdminBillingConfig> {
    return this.http
      .get<AdminBillingConfig>(`${BE_API_AUTH_URL}/admin/billing-config`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  updateBillingConfig(payload: AdminBillingConfig): Observable<AdminBillingConfig> {
    return this.http
      .put<AdminBillingConfig>(`${BE_API_AUTH_URL}/admin/billing-config`, payload, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  uploadBillingConfigProviderAsset(type: 'signatureStamp' | 'signature' | 'stamp', fileBase64: string): Observable<AdminBillingConfig> {
    return this.http
      .post<AdminBillingConfig>(`${BE_API_AUTH_URL}/admin/billing-config/provider-asset`, { type, fileBase64 }, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  removeBillingConfigProviderAsset(type: 'signatureStamp' | 'signature' | 'stamp'): Observable<AdminBillingConfig> {
    return this.http
      .delete<AdminBillingConfig>(`${BE_API_AUTH_URL}/admin/billing-config/provider-asset`, {
        headers: this.authService.createAuthorizationHeader(),
        body: { type },
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getBillingConfigProviderAssetPreviewUrl(type: 'signatureStamp' | 'signature' | 'stamp'): Observable<{ url: string }> {
    return this.http
      .get<{ url: string }>(`${BE_API_AUTH_URL}/admin/billing-config/provider-asset/${type}/preview`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  listBillingActions(params: {
    status?: string;
    type?: string;
    companyId?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  } = {}): Observable<BillingActionListResponse> {
    let httpParams = new HttpParams();
    if (params.status && params.status !== 'all') {
      httpParams = httpParams.set('status', params.status);
    }
    if (params.type && params.type !== 'all') {
      httpParams = httpParams.set('type', params.type);
    }
    if (params.companyId) {
      httpParams = httpParams.set('companyId', params.companyId);
    }
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (typeof params.page === 'number') {
      httpParams = httpParams.set('page', String(params.page));
    }
    if (typeof params.pageSize === 'number') {
      httpParams = httpParams.set('pageSize', String(params.pageSize));
    }
    return this.http
      .get<BillingActionListResponse>(`${BE_API_AUTH_URL}/admin/billing-actions`, {
        headers: this.authService.createAuthorizationHeader(),
        params: httpParams,
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  countBillingActions(): Observable<BillingActionCountResponse> {
    return this.http
      .get<BillingActionCountResponse>(`${BE_API_AUTH_URL}/admin/billing-actions/count`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getBillingAction(id: string): Observable<BillingActionItem> {
    return this.http
      .get<BillingActionItem>(`${BE_API_AUTH_URL}/admin/billing-actions/${id}`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  markBillingActionHandled(id: string, internalNote?: string): Observable<BillingActionItem> {
    const body: any = {};
    if (typeof internalNote === 'string' && internalNote.trim()) {
      body.internalNote = internalNote.trim();
    }
    return this.http
      .patch<BillingActionItem>(`${BE_API_AUTH_URL}/admin/billing-actions/${id}/handle`, body, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  reopenBillingAction(id: string): Observable<BillingActionItem> {
    return this.http
      .patch<BillingActionItem>(`${BE_API_AUTH_URL}/admin/billing-actions/${id}/reopen`, {}, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  dismissBillingAction(id: string, internalNote?: string): Observable<BillingActionItem> {
    const body: any = {};
    if (typeof internalNote === 'string' && internalNote.trim()) {
      body.internalNote = internalNote.trim();
    }
    return this.http
      .patch<BillingActionItem>(`${BE_API_AUTH_URL}/admin/billing-actions/${id}/dismiss`, body, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  updateBillingActionNote(id: string, internalNote: string): Observable<BillingActionItem> {
    return this.http
      .patch<BillingActionItem>(`${BE_API_AUTH_URL}/admin/billing-actions/${id}/note`, { internalNote }, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }
}
